/**
 * Created by WorkAxis www.workaxis.in
 * Admin: shyam@workaxis.in
 * Project: W-CRM
 *
 * State for the AI agent training console. Talks to the CRM proxy under
 * /integration/ai/, which forwards to the AI service and injects org_id
 * from the authenticated session (never sent from the client).
 */
import {createSlice} from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import {formatAxiosErrorMessage} from "../../utils/common";
import {endPoints} from "../../api/endpoints";
import {apiUrlBuilder} from "../../utils/util";

// The proxy forwards the AI service payload untouched, but other CRM
// endpoints wrap responses in {data}. Accept either shape.
const unwrap = (payload) => payload?.data ?? payload ?? null;

const unwrapList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

/** Mirrors ai-service defaults so the drawer renders before GET resolves. */
export const DEFAULT_ORG_AI_SETTINGS = {
    agent_name: "",
    tone: "human",
    reply_length: "short",
    emoji: "light",
    disclose_ai: false,
    language_mode: "mirror",
    primary_objective: "",
    greeting_message: "",
    sign_off_message: "",
    must_do: [],
    never_do: [],
    terminology: [],
    use_org_name: false,
    agent_role: "sales",
    qualification_style: "light",
    closing_style: "soft",
    // WhatsApp suggestion chips: always | minimal | chatbot_only
    suggestion_mode: "minimal",
    // CRM-synced, read-only for the drawer
    org_name: "",
    nudge_enabled: false,
    nudge_delays_minutes: [30, 180],
    nudge_quiet_start: "21:00",
    nudge_quiet_end: "09:00",
    timezone: "Asia/Kolkata",
    nudge_min_temperature: "any",
};

/** Keep only known setting keys from an API payload on top of the previous state. */
export const mergeOrgAiSettings = (previous, payload) => {
    const body = unwrap(payload) || {};
    const next = {...DEFAULT_ORG_AI_SETTINGS, ...(previous || {})};
    Object.keys(DEFAULT_ORG_AI_SETTINGS).forEach((key) => {
        if (body[key] !== undefined && body[key] !== null) next[key] = body[key];
    });
    return next;
};

/** Default page size for GET /integration/ai/train/feedback/ */
export const AI_FEEDBACK_PAGE_SIZE = 20;

/**
 * Normalise the paginated feedback envelope:
 * { items, total, limit, offset } — also tolerates a legacy bare array.
 */
const unwrapFeedbackPage = (payload, fallbackLimit = AI_FEEDBACK_PAGE_SIZE) => {
    const body = unwrap(payload);
    if (Array.isArray(body)) {
        return {
            items: body,
            total: body.length,
            limit: fallbackLimit,
            offset: 0,
        };
    }
    const items = Array.isArray(body?.items)
        ? body.items
        : Array.isArray(body?.results)
            ? body.results
            : [];
    return {
        items,
        total: Number(body?.total) || items.length,
        limit: Number(body?.limit) || fallbackLimit,
        offset: Number(body?.offset) || 0,
    };
};

const AiAgentInitialState = {
    sources: {
        isLoading: false,
        // Set once the first fetch settles, so the guide knows when it can
        // decide between a first-run and a returning greeting.
        loaded: false,
        data: [],
        error: null,
    },
    // Org-level AI capability flags on Organization (loading tracked per flag).
    power: {
        loading: {
            reply: false,
            generation: false,
            stageUpdate: false,
        },
        error: null,
    },
    // Assistant persona + conversation nudges from GET/PUT /integration/ai/org-settings/.
    orgSettings: {
        isLoading: false,
        isSaving: false,
        loaded: false,
        agentName: "",
        // Full snake_case settings object as returned by the AI service
        settings: {...DEFAULT_ORG_AI_SETTINGS},
        error: null,
        saveError: null,
    },
    trainWebsite: {
        isLoading: false,
        data: null,
        error: null,
    },
    trainUpload: {
        isLoading: false,
        data: null,
        error: null,
        progress: 0,
    },
    trainText: {
        isLoading: false,
        data: null,
        error: null,
    },
    deleteSource: {
        isLoading: false,
        data: null,
        error: null,
    },
    feedbackList: {
        isLoading: false,
        data: [],
        total: 0,
        limit: AI_FEEDBACK_PAGE_SIZE,
        offset: 0,
        error: null,
    },
    createFeedback: {
        isLoading: false,
        data: null,
        error: null,
    },
    deleteFeedback: {
        isLoading: false,
        data: null,
        error: null,
    },
    gapList: {
        isLoading: false,
        data: [],
        total: 0,
        limit: AI_FEEDBACK_PAGE_SIZE,
        offset: 0,
        error: null,
    },
    answerGap: {
        isLoading: false,
        data: null,
        error: null,
    },
    ignoreGap: {
        isLoading: false,
        data: null,
        error: null,
    },
};

export const AiAgentSlice = createSlice({
    name: "aiAgent",
    initialState: {
        ...AiAgentInitialState,
    },
    reducers: {
        AiSourcesRequesting: (state) => {
            state.sources.isLoading = true;
        },
        AiSourcesSuccess: (state, action) => {
            state.sources.isLoading = false;
            state.sources.loaded = true;
            state.sources.error = null;
            state.sources.data = unwrapList(action.payload);
        },
        AiSourcesError: (state, action) => {
            state.sources.isLoading = false;
            state.sources.loaded = true;
            state.sources.error = action.payload;
        },

        AiTrainWebsiteRequesting: (state) => {
            state.trainWebsite.isLoading = true;
            state.trainWebsite.error = null;
        },
        AiTrainWebsiteSuccess: (state, action) => {
            state.trainWebsite.isLoading = false;
            state.trainWebsite.error = null;
            state.trainWebsite.data = action.payload;
        },
        AiTrainWebsiteError: (state, action) => {
            state.trainWebsite.isLoading = false;
            state.trainWebsite.error = action.payload;
        },
        AiTrainWebsiteReset: (state) => {
            state.trainWebsite = AiAgentInitialState.trainWebsite;
        },

        AiTrainUploadRequesting: (state) => {
            state.trainUpload.isLoading = true;
            state.trainUpload.error = null;
            state.trainUpload.progress = 0;
        },
        AiTrainUploadProgress: (state, action) => {
            state.trainUpload.progress = action.payload;
        },
        AiTrainUploadSuccess: (state, action) => {
            state.trainUpload.isLoading = false;
            state.trainUpload.error = null;
            state.trainUpload.progress = 100;
            state.trainUpload.data = action.payload;
        },
        AiTrainUploadError: (state, action) => {
            state.trainUpload.isLoading = false;
            state.trainUpload.progress = 0;
            state.trainUpload.error = action.payload;
        },
        AiTrainUploadReset: (state) => {
            state.trainUpload = AiAgentInitialState.trainUpload;
        },

        AiTrainTextRequesting: (state) => {
            state.trainText.isLoading = true;
            state.trainText.error = null;
        },
        AiTrainTextSuccess: (state, action) => {
            state.trainText.isLoading = false;
            state.trainText.error = null;
            state.trainText.data = action.payload;
        },
        AiTrainTextError: (state, action) => {
            state.trainText.isLoading = false;
            state.trainText.error = action.payload;
        },
        AiTrainTextReset: (state) => {
            state.trainText = AiAgentInitialState.trainText;
        },

        AiDeleteSourceRequesting: (state) => {
            state.deleteSource.isLoading = true;
            state.deleteSource.error = null;
        },
        AiDeleteSourceSuccess: (state, action) => {
            state.deleteSource.isLoading = false;
            state.deleteSource.error = null;
            state.deleteSource.data = action.payload;
            state.sources.data = state.sources.data.filter(
                (source) => String(source.id) !== String(action.payload)
            );
        },
        AiDeleteSourceError: (state, action) => {
            state.deleteSource.isLoading = false;
            state.deleteSource.error = action.payload;
        },
        AiDeleteSourceReset: (state) => {
            state.deleteSource = AiAgentInitialState.deleteSource;
        },

        AiFeedbackListRequesting: (state) => {
            state.feedbackList.isLoading = true;
        },
        AiFeedbackListSuccess: (state, action) => {
            state.feedbackList.isLoading = false;
            state.feedbackList.error = null;
            const page = action.payload || {};
            state.feedbackList.data = page.items || [];
            state.feedbackList.total = page.total ?? 0;
            state.feedbackList.limit = page.limit ?? AI_FEEDBACK_PAGE_SIZE;
            state.feedbackList.offset = page.offset ?? 0;
        },
        AiFeedbackListError: (state, action) => {
            state.feedbackList.isLoading = false;
            state.feedbackList.error = action.payload;
        },

        AiCreateFeedbackRequesting: (state) => {
            state.createFeedback.isLoading = true;
            state.createFeedback.error = null;
        },
        AiCreateFeedbackSuccess: (state, action) => {
            state.createFeedback.isLoading = false;
            state.createFeedback.error = null;
            state.createFeedback.data = action.payload;
        },
        AiCreateFeedbackError: (state, action) => {
            state.createFeedback.isLoading = false;
            state.createFeedback.error = action.payload;
        },
        AiCreateFeedbackReset: (state) => {
            state.createFeedback = AiAgentInitialState.createFeedback;
        },

        AiDeleteFeedbackRequesting: (state) => {
            state.deleteFeedback.isLoading = true;
            state.deleteFeedback.error = null;
        },
        AiDeleteFeedbackSuccess: (state, action) => {
            state.deleteFeedback.isLoading = false;
            state.deleteFeedback.error = null;
            state.deleteFeedback.data = action.payload;
            const before = state.feedbackList.data.length;
            state.feedbackList.data = state.feedbackList.data.filter(
                (item) => String(item.id) !== String(action.payload)
            );
            if (state.feedbackList.data.length < before) {
                state.feedbackList.total = Math.max(0, state.feedbackList.total - 1);
            }
        },
        AiDeleteFeedbackError: (state, action) => {
            state.deleteFeedback.isLoading = false;
            state.deleteFeedback.error = action.payload;
        },
        AiDeleteFeedbackReset: (state) => {
            state.deleteFeedback = AiAgentInitialState.deleteFeedback;
        },

        AiGapListRequesting: (state) => {
            state.gapList.isLoading = true;
        },
        AiGapListSuccess: (state, action) => {
            state.gapList.isLoading = false;
            state.gapList.error = null;
            const page = action.payload || {};
            state.gapList.data = page.items || [];
            state.gapList.total = page.total ?? 0;
            state.gapList.limit = page.limit ?? AI_FEEDBACK_PAGE_SIZE;
            state.gapList.offset = page.offset ?? 0;
        },
        AiGapListError: (state, action) => {
            state.gapList.isLoading = false;
            state.gapList.error = action.payload;
        },

        AiAnswerGapRequesting: (state) => {
            state.answerGap.isLoading = true;
            state.answerGap.error = null;
        },
        AiAnswerGapSuccess: (state, action) => {
            state.answerGap.isLoading = false;
            state.answerGap.error = null;
            state.answerGap.data = action.payload;
        },
        AiAnswerGapError: (state, action) => {
            state.answerGap.isLoading = false;
            state.answerGap.error = action.payload;
        },
        AiAnswerGapReset: (state) => {
            state.answerGap = AiAgentInitialState.answerGap;
        },

        AiIgnoreGapRequesting: (state) => {
            state.ignoreGap.isLoading = true;
            state.ignoreGap.error = null;
        },
        AiIgnoreGapSuccess: (state, action) => {
            state.ignoreGap.isLoading = false;
            state.ignoreGap.error = null;
            state.ignoreGap.data = action.payload;
            const before = state.gapList.data.length;
            state.gapList.data = state.gapList.data.filter(
                (item) => String(item.id) !== String(action.payload)
            );
            if (state.gapList.data.length < before) {
                state.gapList.total = Math.max(0, state.gapList.total - 1);
            }
        },
        AiIgnoreGapError: (state, action) => {
            state.ignoreGap.isLoading = false;
            state.ignoreGap.error = action.payload;
        },
        AiIgnoreGapReset: (state) => {
            state.ignoreGap = AiAgentInitialState.ignoreGap;
        },

        AiPowerRequesting: (state, action) => {
            const key = action.payload;
            if (key && state.power.loading[key] !== undefined) {
                state.power.loading[key] = true;
            }
            state.power.error = null;
        },
        AiPowerSuccess: (state, action) => {
            const key = action.payload;
            if (key && state.power.loading[key] !== undefined) {
                state.power.loading[key] = false;
            }
            state.power.error = null;
        },
        AiPowerError: (state, action) => {
            const {key, error} = action.payload || {};
            if (key && state.power.loading[key] !== undefined) {
                state.power.loading[key] = false;
            }
            state.power.error = error ?? action.payload;
        },

        AiOrgSettingsRequesting: (state) => {
            state.orgSettings.isLoading = true;
            state.orgSettings.error = null;
        },
        AiOrgSettingsSuccess: (state, action) => {
            state.orgSettings.isLoading = false;
            state.orgSettings.loaded = true;
            state.orgSettings.error = null;
            state.orgSettings.agentName = (action.payload?.agent_name || "").trim();
            state.orgSettings.settings = mergeOrgAiSettings(
                state.orgSettings.settings,
                action.payload
            );
        },
        AiOrgSettingsError: (state, action) => {
            state.orgSettings.isLoading = false;
            state.orgSettings.loaded = true;
            state.orgSettings.error = action.payload;
        },

        AiOrgSettingsSaveRequesting: (state) => {
            state.orgSettings.isSaving = true;
            state.orgSettings.saveError = null;
        },
        AiOrgSettingsSaveSuccess: (state, action) => {
            state.orgSettings.isSaving = false;
            state.orgSettings.saveError = null;
            state.orgSettings.settings = mergeOrgAiSettings(
                state.orgSettings.settings,
                action.payload
            );
            state.orgSettings.agentName = (
                state.orgSettings.settings.agent_name || ""
            ).trim();
        },
        AiOrgSettingsSaveError: (state, action) => {
            state.orgSettings.isSaving = false;
            state.orgSettings.saveError = action.payload;
        },

        AiAgentResetState: () => ({...AiAgentInitialState}),
    },
});

/**
 * @param silent when true the list refreshes without toggling the loading
 *               flag, so background polling does not flash the UI.
 */
export const AiSourcesAPI = (silent = false) => async (dispatch) => {
    if (!silent) {
        dispatch(AiSourcesRequesting());
    }
    try {
        const response = await apiService.get(endPoints.integration.ai.sources, {});
        dispatch(AiSourcesSuccess(unwrap(response.data)));
    } catch (error) {
        dispatch(AiSourcesError(formatAxiosErrorMessage(error)));
    }
};

export const AiTrainWebsiteAPI = (payload) => async (dispatch) => {
    dispatch(AiTrainWebsiteRequesting());
    try {
        const response = await apiService.post(
            endPoints.integration.ai.trainWebsite,
            payload
        );
        dispatch(AiTrainWebsiteSuccess(unwrap(response.data)));
        dispatch(AiSourcesAPI(true));
    } catch (error) {
        dispatch(AiTrainWebsiteError(formatAxiosErrorMessage(error)));
    }
};

export const AiTrainUploadAPI = (file) => async (dispatch) => {
    dispatch(AiTrainUploadRequesting());
    try {
        const body = new FormData();
        body.append("file", file, file.name);
        const response = await apiService.post(
            endPoints.integration.ai.trainUpload,
            body,
            {
                headers: {"Content-Type": "multipart/form-data"},
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    // Cap at 99 so the bar only completes once the server responds.
                    const percent = Math.min(
                        99,
                        Math.round((event.loaded * 100) / event.total)
                    );
                    dispatch(AiTrainUploadProgress(percent));
                },
            }
        );
        dispatch(AiTrainUploadSuccess(unwrap(response.data)));
        dispatch(AiSourcesAPI(true));
    } catch (error) {
        dispatch(AiTrainUploadError(formatAxiosErrorMessage(error)));
    }
};

export const AiTrainTextAPI = (payload) => async (dispatch) => {
    dispatch(AiTrainTextRequesting());
    try {
        const response = await apiService.post(
            endPoints.integration.ai.trainText,
            payload
        );
        dispatch(AiTrainTextSuccess(unwrap(response.data)));
        dispatch(AiSourcesAPI(true));
    } catch (error) {
        dispatch(AiTrainTextError(formatAxiosErrorMessage(error)));
    }
};

export const AiDeleteSourceAPI = (id) => async (dispatch) => {
    dispatch(AiDeleteSourceRequesting());
    try {
        await apiService.delete(
            apiUrlBuilder(endPoints.integration.ai.source, {id}, null),
            {}
        );
        dispatch(AiDeleteSourceSuccess(id));
    } catch (error) {
        dispatch(AiDeleteSourceError(formatAxiosErrorMessage(error)));
    }
};

/**
 * @param {{limit?: number, offset?: number}} [page]
 */
export const AiFeedbackListAPI = (page = {}) => async (dispatch) => {
    const limit = Math.min(100, Math.max(1, page.limit ?? AI_FEEDBACK_PAGE_SIZE));
    const offset = Math.max(0, page.offset ?? 0);
    dispatch(AiFeedbackListRequesting());
    try {
        const query = new URLSearchParams({
            limit: String(limit),
            offset: String(offset),
        });
        const response = await apiService.get(
            `${endPoints.integration.ai.feedback}?${query.toString()}`,
            {}
        );
        dispatch(
            AiFeedbackListSuccess(unwrapFeedbackPage(response.data, limit))
        );
    } catch (error) {
        dispatch(AiFeedbackListError(formatAxiosErrorMessage(error)));
    }
};

export const AiCreateFeedbackAPI = (payload) => async (dispatch) => {
    dispatch(AiCreateFeedbackRequesting());
    try {
        const response = await apiService.post(
            endPoints.integration.ai.feedback,
            payload
        );
        dispatch(AiCreateFeedbackSuccess(unwrap(response.data)));
        // New corrections land on the first page.
        dispatch(AiFeedbackListAPI({limit: AI_FEEDBACK_PAGE_SIZE, offset: 0}));
    } catch (error) {
        dispatch(AiCreateFeedbackError(formatAxiosErrorMessage(error)));
    }
};

export const AiDeleteFeedbackAPI = (id) => async (dispatch) => {
    dispatch(AiDeleteFeedbackRequesting());
    try {
        await apiService.delete(
            apiUrlBuilder(endPoints.integration.ai.feedbackItem, {id}, null),
            {}
        );
        dispatch(AiDeleteFeedbackSuccess(id));
    } catch (error) {
        dispatch(AiDeleteFeedbackError(formatAxiosErrorMessage(error)));
    }
};

/**
 * @param {{limit?: number, offset?: number, status?: string}} [page]
 */
export const AiGapListAPI = (page = {}) => async (dispatch) => {
    const limit = Math.min(100, Math.max(1, page.limit ?? AI_FEEDBACK_PAGE_SIZE));
    const offset = Math.max(0, page.offset ?? 0);
    const statusFilter = page.status || "open";
    dispatch(AiGapListRequesting());
    try {
        const query = new URLSearchParams({
            limit: String(limit),
            offset: String(offset),
            status: statusFilter,
        });
        const response = await apiService.get(
            `${endPoints.integration.ai.gaps}?${query.toString()}`,
            {}
        );
        dispatch(AiGapListSuccess(unwrapFeedbackPage(response.data, limit)));
    } catch (error) {
        dispatch(AiGapListError(formatAxiosErrorMessage(error)));
    }
};

export const AiAnswerGapAPI = (id, correctAnswer) => async (dispatch) => {
    dispatch(AiAnswerGapRequesting());
    try {
        const response = await apiService.post(
            apiUrlBuilder(endPoints.integration.ai.gapAnswer, {id}, null),
            {correct_answer: correctAnswer}
        );
        dispatch(AiAnswerGapSuccess(unwrap(response.data)));
        dispatch(AiGapListAPI({limit: AI_FEEDBACK_PAGE_SIZE, offset: 0}));
        dispatch(AiFeedbackListAPI({limit: AI_FEEDBACK_PAGE_SIZE, offset: 0}));
    } catch (error) {
        dispatch(AiAnswerGapError(formatAxiosErrorMessage(error)));
    }
};

export const AiIgnoreGapAPI = (id) => async (dispatch) => {
    dispatch(AiIgnoreGapRequesting());
    try {
        await apiService.post(
            apiUrlBuilder(endPoints.integration.ai.gapIgnore, {id}, null),
            {}
        );
        dispatch(AiIgnoreGapSuccess(id));
    } catch (error) {
        dispatch(AiIgnoreGapError(formatAxiosErrorMessage(error)));
    }
};

/**
 * Patch Organization AI flags without a full page reload.
 * Accepts any of:
 *   is_aiservice_reply_enabled — chatbot sync + customer replies
 *   is_aiservice_generation_enabled — client-summary / insights generation
 *   is_aiservice_stage_update_enabled — AI pipeline stage movement
 * `flagKey` is "reply" | "generation" | "stageUpdate" so only that toggle shows busy.
 * Caller should refresh the profile so UI stays in sync.
 */
export const AiAgentSetFlagsAPI = (orgId, flagKey, flags) => async (dispatch) => {
    dispatch(AiPowerRequesting(flagKey));
    try {
        await apiService.patch(`${endPoints.organization.get}${orgId}/`, flags);
        dispatch(AiPowerSuccess(flagKey));
        return true;
    } catch (error) {
        dispatch(
            AiPowerError({
                key: flagKey,
                error: formatAxiosErrorMessage(error),
            })
        );
        return false;
    }
};

/** Load org AI persona settings (agent_name). CRM injects org_id. */
export const AiOrgSettingsAPI = () => async (dispatch) => {
    dispatch(AiOrgSettingsRequesting());
    try {
        const response = await apiService.get(endPoints.integration.ai.orgSettings, {});
        dispatch(AiOrgSettingsSuccess(unwrap(response.data) || {}));
    } catch (error) {
        dispatch(AiOrgSettingsError(formatAxiosErrorMessage(error)));
    }
};

/**
 * Partially update org AI settings (persona and/or follow-ups).
 * Send only changed snake_case keys — never org_id (CRM injects it).
 * Returns { ok: true } or { ok: false, error }.
 */
export const AiOrgSettingsPatchAPI = (partial) => async (dispatch) => {
    const body = {...(partial || {})};
    if (!Object.keys(body).length) return {ok: true};
    dispatch(AiOrgSettingsSaveRequesting());
    try {
        const response = await apiService.put(endPoints.integration.ai.orgSettings, body);
        dispatch(AiOrgSettingsSaveSuccess(unwrap(response.data) || body));
        return {ok: true};
    } catch (error) {
        const message = formatAxiosErrorMessage(error);
        dispatch(AiOrgSettingsSaveError(message));
        return {ok: false, error: message};
    }
};

/**
 * Upsert assistant display name (rename from the header / name card).
 * Returns { ok: true } or { ok: false, error }.
 */
export const AiOrgSettingsUpdateAPI = (agentName) => async (dispatch) => {
    const name = (agentName || "").trim();
    if (!name) {
        const error = "Give your assistant a name.";
        dispatch(AiOrgSettingsSaveError(error));
        return {ok: false, error};
    }
    return dispatch(AiOrgSettingsPatchAPI({agent_name: name.slice(0, 80)}));
};

export const {
    AiSourcesRequesting,
    AiSourcesSuccess,
    AiSourcesError,

    AiTrainWebsiteRequesting,
    AiTrainWebsiteSuccess,
    AiTrainWebsiteError,
    AiTrainWebsiteReset,

    AiTrainUploadRequesting,
    AiTrainUploadProgress,
    AiTrainUploadSuccess,
    AiTrainUploadError,
    AiTrainUploadReset,

    AiTrainTextRequesting,
    AiTrainTextSuccess,
    AiTrainTextError,
    AiTrainTextReset,

    AiDeleteSourceRequesting,
    AiDeleteSourceSuccess,
    AiDeleteSourceError,
    AiDeleteSourceReset,

    AiFeedbackListRequesting,
    AiFeedbackListSuccess,
    AiFeedbackListError,

    AiCreateFeedbackRequesting,
    AiCreateFeedbackSuccess,
    AiCreateFeedbackError,
    AiCreateFeedbackReset,

    AiDeleteFeedbackRequesting,
    AiDeleteFeedbackSuccess,
    AiDeleteFeedbackError,
    AiDeleteFeedbackReset,

    AiGapListRequesting,
    AiGapListSuccess,
    AiGapListError,

    AiAnswerGapRequesting,
    AiAnswerGapSuccess,
    AiAnswerGapError,
    AiAnswerGapReset,

    AiIgnoreGapRequesting,
    AiIgnoreGapSuccess,
    AiIgnoreGapError,
    AiIgnoreGapReset,

    AiPowerRequesting,
    AiPowerSuccess,
    AiPowerError,

    AiOrgSettingsRequesting,
    AiOrgSettingsSuccess,
    AiOrgSettingsError,
    AiOrgSettingsSaveRequesting,
    AiOrgSettingsSaveSuccess,
    AiOrgSettingsSaveError,

    AiAgentResetState,
} = AiAgentSlice.actions;

export const AiAgentState = (state) => state.aiAgent;
export default AiAgentSlice.reducer;
